import React, { useState, useEffect } from "react";
import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { motion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";
import { getCurrentFactionMonth, getFactionName, FACTIONS } from "./utils/factionLogic";

const FACTION_EMOJIS = {
  bracer: "🛡️",
  ouroboros: "🐍",
  church: "⛪",
};

function PlayerRow({ rank, player, isCurrentPlayer }) {
  const faction = FACTIONS.includes(player.faction) ? player.faction : null;
  const playerLabel = isCurrentPlayer
    ? "You"
    : `Player #${player.shortId}`;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        borderRadius: "8px",
        backgroundColor: isCurrentPlayer ? "var(--era-accent-dim, rgba(74, 144, 226, 0.15))" : "transparent",
        border: isCurrentPlayer ? "1px solid var(--era-accent, #4a90e2)" : "1px solid transparent",
        marginBottom: "4px",
        gap: "12px",
      }}
    >
      <span style={{ width: "30px", fontWeight: "bold", textAlign: "center", fontSize: "1.1rem" }}>
        {rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`}
      </span>
      {faction && (
        <span style={{ fontSize: "1.1rem" }} title={getFactionName(faction)}>
          {FACTION_EMOJIS[faction]}
        </span>
      )}
      <span style={{ flex: 1, fontWeight: isCurrentPlayer ? "bold" : "normal" }}>
        {playerLabel}
      </span>
      <span style={{ fontWeight: "bold", fontSize: "1.05rem" }}>
        {player.monthPoints}
      </span>
    </div>
  );
}

export default function LeaderboardModal({ onClose, currentTheme }) {
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPlayerId, setCurrentPlayerId] = useState("");

  useEffect(() => {
    setCurrentPlayerId(localStorage.getItem("kisekidle-player-id") || "");
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const month = getCurrentFactionMonth();
        const q = query(
          collection(db, "player-scores"),
          where("month", "==", month),
          orderBy("monthPoints", "desc"),
          limit(50)
        );
        const snap = await getDocs(q);
        if (!isMounted) return;

        const results = snap.docs.map((doc) => ({
          id: doc.id,
          shortId: doc.id.slice(0, 8),
          ...doc.data(),
          monthPoints: doc.data().monthPoints || 0,
        }));

        setPlayers(results);
      } catch (e) {
        console.error("Error fetching leaderboard:", e);
        if (isMounted) {
          setError("Could not load leaderboard. The index may still be building — try again in a moment.");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchLeaderboard();
    return () => { isMounted = false; };
  }, []);

  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay forceMount asChild>
          <motion.div
            className={`modal-overlay theme-${currentTheme}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </Dialog.Overlay>
        <div className={`modal-content-wrapper theme-${currentTheme}`}>
          <Dialog.Content forceMount asChild>
            <motion.div
              className="modal-content global-stats-box"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Dialog.Title className="sr-only">Player Leaderboard</Dialog.Title>
              <Dialog.Description className="sr-only">
                Top players ranked by monthly faction points.
              </Dialog.Description>

              <Dialog.Close asChild>
                <button className="modal-close">✖</button>
              </Dialog.Close>

              <h2 className="mode-title" style={{ textAlign: "center", marginBottom: "8px" }}>
                🏆 Player Leaderboard
              </h2>
              <p style={{ textAlign: "center", opacity: 0.7, fontSize: "0.85rem", marginBottom: "20px" }}>
                Monthly rankings based on faction points — {getCurrentFactionMonth()}
              </p>

              {isLoading ? (
                <p style={{ textAlign: "center" }}>Loading leaderboard...</p>
              ) : error ? (
                <p style={{ textAlign: "center", color: "#ff6b6b" }}>{error}</p>
              ) : players.length === 0 ? (
                <p style={{ textAlign: "center", opacity: 0.6, fontStyle: "italic" }}>
                  No players yet this month. Play a game to appear!
                </p>
              ) : (
                <div>
                  {players.map((player, i) => (
                    <PlayerRow
                      key={player.id}
                      rank={i + 1}
                      player={player}
                      isCurrentPlayer={player.id === currentPlayerId}
                    />
                  ))}
                </div>
              )}

              {currentPlayerId && !players.find((p) => p.id === currentPlayerId) && !isLoading && !error && (
                <div style={{ marginTop: "16px", padding: "10px", borderRadius: "8px", backgroundColor: "rgba(0,0,0,0.1)", textAlign: "center", opacity: 0.6, fontStyle: "italic" }}>
                  You haven't scored any points this month. Play a game to join!
                </div>
              )}
            </motion.div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
